import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import OpenDoorHeader from './OpenDoorHeader';
import OpenDoorContactSection from './OpenDoorContactSection';
import OpenDoorIntroductionSection from './OpenDoorIntroductionSection';
import OpenDoorResearchSection from './OpenDoorResearchSection';
import { CHAR_LIMIT, INITIAL_CONTACT_FORM } from './constants';
import { canSaveProspect, formToProspectPayload, prospectToFormFields } from '../prospectUtils';
import { useCreateProspect, useUpdateProspect } from '../../../queries/prospects';

const ClientWorkspaceOpenDoor = ({ companyName = 'this company', onBack }) => {
  const [form, setForm] = useState(INITIAL_CONTACT_FORM);
  const formRef = useRef(form);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const selectedProspectIdRef = useRef(null);

  const { mutateAsync: createProspect, isPending: isCreating } = useCreateProspect();
  const { mutateAsync: updateProspect, isPending: isUpdating } = useUpdateProspect();
  const isGenerating = isCreating || isUpdating;

  const setFormState = (updater) => {
    setForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formRef.current = next;
      return next;
    });
  };

  const setSelectedId = (id) => {
    selectedProspectIdRef.current = id;
    setSelectedProspectId(id);
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value ?? '';
    setFormState((prev) => ({
      ...prev,
      [field]: value.length <= CHAR_LIMIT ? value : value.slice(0, CHAR_LIMIT),
    }));
    if (field === 'fullName') {
      setSelectedId(null);
    }
  };

  const handleGenerate = async () => {
    const currentForm = formRef.current;
    const currentSelectedId = selectedProspectIdRef.current;

    if (!canSaveProspect(currentForm)) {
      toast('Full name and company are required.', { type: 'warning' });
      return;
    }

    try {
      const payload = formToProspectPayload(currentForm);
      const isUpdate = Boolean(currentSelectedId);
      const saved = isUpdate
        ? await updateProspect({ id: currentSelectedId, data: payload })
        : await createProspect({ data: payload });

      setSelectedId(saved._id);
      setFormState((prev) => ({
        ...prev,
        ...prospectToFormFields(saved),
      }));
      toast(isUpdate ? 'Prospect updated.' : 'Prospect saved.', { type: 'success' });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to save prospect.';
      toast(message, { type: 'error' });
    }
  };

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0} bgcolor='#fff'>
      <Box flex={1} overflow='auto' px={{ xs: 2.5, md: 4, xl: 5 }} pt={3} pb={4} minHeight={0}>
        <OpenDoorHeader companyName={companyName} onBack={onBack} />

        <Box
          display='grid'
          gridTemplateColumns={{
            xs: '1fr',
            md: 'minmax(260px, 1fr) minmax(300px, 2fr) minmax(260px, 1fr)',
          }}
          gap={2.5}
          alignItems='start'
        >
          <OpenDoorContactSection
            companyName={companyName}
            form={form}
            onFieldChange={handleFieldChange}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            canGenerate={canSaveProspect(form)}
          />
          <OpenDoorIntroductionSection />
          <OpenDoorResearchSection companyName={companyName} />
        </Box>
      </Box>
    </Box>
  );
};

export default ClientWorkspaceOpenDoor;
